import {FC, useMemo, useState} from 'react';
import dayjs, {Dayjs} from 'dayjs';
//formik
import {FieldInputProps, FormikProps} from 'formik';
//mui
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {TimeField as MuiTimeField, LocalizationProvider} from '@mui/x-date-pickers';
import {Box, InputAdornment, SelectChangeEvent, SxProps, Theme} from '@mui/material';
//types
import {SelectOptionsType} from '@/utils/types';
//constants
import {H_M, OUTLINED, START, ZERO_TIME} from '@/utils/constants';
//icons
import {ReactComponent as ClockIcon} from '@icons/clock.svg';
//components
import Select from '../Select';
import FormHelperText from '../FormHelperText';
import InputLabel from '../Inputs/InputLabel';
//helpers
import {defaultMaxTimeValue, getChangingTime, getMeridiemChangingTime, meridiemOptions} from '@/utils/helpers';
//styles
import {timePickerStyle} from './TimePicker.styles';

interface TimePickerProps {
	field: FieldInputProps<string | number>;
	form: FormikProps<any>;
	label?: string;
	sx?: SxProps<Theme>;
	parentClassName?: SxProps<Theme>;
	disabled?: boolean;
	disableErrorMessage?: boolean;
}

const TimePicker: FC<TimePickerProps> = ({
	field,
	form,
	label,
	disabled,
	sx,
	parentClassName,
	disableErrorMessage,
	...rest
}) => {
	const meridiem =
		meridiemOptions.find((item) => item.name === (field.value as string)?.split(' ')[1])?.id ||
		(field.value as string)?.split(' ')[1];

	const [currentMeridiem, setCurrentMeridiem] = useState<string>(meridiem || meridiemOptions[1].id);

	const [isTimeValid, setIsTimeValid] = useState<boolean>(true);

	const errorText = useMemo(() => {
		return (field?.name && form?.touched[field?.name] && (form?.errors[field?.name] as string)) || undefined;
	}, [form?.errors, form?.touched, field]);

	const isError = useMemo(() => !!errorText || !isTimeValid, [errorText, isTimeValid]);

	const timeValue = useMemo(() => {
		return field.value && dayjs(field.value, 'h:mm A').isValid() ? dayjs(field.value, 'h:mm A') : null;
	}, [field.value]);

	const handleTimeChange = (value: Dayjs | null, context: any) => {
		if (context?.validationError) {
			setIsTimeValid(false);
			return;
		}

		const meridiem = meridiemOptions.find((meridiem) => meridiem.id === currentMeridiem) as SelectOptionsType;
		const time = getChangingTime(value, meridiem.name as string);

		form.setFieldValue(field.name, time);
		setIsTimeValid(true);
	};

	const handleMeridiemChange = (_: SelectChangeEvent, option: SelectOptionsType) => {
		const time = getMeridiemChangingTime(option.name, field.value);

		form.setFieldValue(field.name, time);
		setCurrentMeridiem(option.id);
	};

	return (
		<Box sx={{...parentClassName, ...timePickerStyle.wrapper}}>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Box sx={timePickerStyle.content}>
					{label && <InputLabel error={isError} label={label} disabled={disabled} />}

					<MuiTimeField
						{...rest}
						sx={{
							...sx,
							...timePickerStyle.time,
							...(!isError && timePickerStyle.timeValid),
							...(disabled && timePickerStyle.timeDisabled),
						}}
						value={timeValue}
						format={H_M}
						onChange={handleTimeChange}
						maxTime={defaultMaxTimeValue}
						slotProps={{
							textField: {
								variant: OUTLINED,
								error: isError,
							},
						}}
						disabled={disabled}
						InputProps={{
							startAdornment: (
								<Box sx={{...(isError && timePickerStyle.timeError)}}>
									<InputAdornment position={START}>
										<ClockIcon />
									</InputAdornment>
								</Box>
							),
							placeholder: ZERO_TIME,
						}}
					/>
				</Box>

				<Box sx={timePickerStyle.meridiem}>
					<Select
						options={meridiemOptions}
						onChange={handleMeridiemChange}
						value={currentMeridiem}
						sx={timePickerStyle.meridiemSelect}
						disabled={disabled}
					/>
				</Box>
			</LocalizationProvider>

			{!disableErrorMessage && errorText && <FormHelperText error title={errorText} />}
		</Box>
	);
};

export default TimePicker;
